'use client';

import React from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  buildProductsQuery,
  type BuildProductQueryOptions,
} from '@eliadmin/core/src/utils/product-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { CategoryTab } from '../ui/category-tab';
import { Button } from '../ui/button';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { ProductsSkeleton } from '../product/products-skeleton';
import { Carousel } from '../ui/carousel';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { ProductCard } from '../product/product-card';

interface ProductsInSaleProps {
  categories: CategoriesEntity[];
  queries?: BuildProductQueryOptions;
  title: string;
  className?: {
    section?: string;
  };
}

export function ProductsInSale({ title, categories, queries }: ProductsInSaleProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const isInitialized = useCustomerStore(s => s.isInitialized);

  const [activeCategoryId, setActiveCategoryId] = React.useState<number>(
    Number(categories[0]?.id ?? 0),
  );

  const activeCategory = categories.find(c => c.id === activeCategoryId);

  const { t } = useTranslations();

  const options = {
    align: 'center' as const,
  };

  const productsQueries = buildProductsQuery({
    ...queries,
    categories: [activeCategoryId],
    showAttributes: true,
    discounted: true,
  });

  const productsQuery = useInfiniteQuery({
    queryKey: [CacheKey.PRODUCTS, 'homepage-infinite', locale, authToken ?? null, productsQueries],
    queryFn: async ({ pageParam }) => {
      return await fetchProducts({
        locale,
        authToken,
        query: {
          ...productsQueries,
          page: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: Number(productsQueries.page ?? 1),
    enabled: isInitialized,
  });

  const products = productsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = productsQuery.isLoading;

  return (
    <section className={cn('custom-section', 'gap-6 bg-[#FF4B00] py-[60px] pr-0 sm:py-16 sm:pr-4')}>
      <div className={cn('custom-container', 'flex items-center justify-start gap-3 pr-4 sm:pr-0')}>
        <Heading className={{ title: 'text-white' }} title={title} tag="h2" />
        <Heading className={{ title: 'text-white/50' }} title={'%'} tag="h2" />
      </div>

      <div
        className={cn('custom-container', 'flex w-full items-center justify-between pr-4 sm:pr-0')}
      >
        <div className="grid w-full grid-cols-3 items-center justify-start bg-white p-1 sm:flex sm:w-fit ">
          {categories.map(category => (
            <CategoryTab
              key={category.slug}
              category={category}
              activeCategoryId={activeCategoryId}
              setActiveCategoryId={setActiveCategoryId}
            />
          ))}
        </div>
        <Button asChild variant={'dark'} className="hidden whitespace-nowrap sm:flex">
          <Link prefetch={false} href={ROUTES.CATEGORY({ slug: activeCategory?.slug || '' })}>
            {t('text3439')}
          </Link>
        </Button>
      </div>
      {isLoading ? (
        <ProductsSkeleton amount={4} />
      ) : (
        <div className={cn('custom-container', 'relative')}>
          <CarouselControls
            className={{
              main: 'absolute top-1/2 z-30 flex w-full -translate-y-1/2 items-center justify-between',
              button:
                'max-h-12 min-h-12 min-w-12 max-w-12 rounded-none border-none bg-black text-white shadow-none hover:bg-black/75',
            }}
            buttonProps={{ size: 'icon', variant: 'dark' }}
          />
          <div
            className={cn(
              'absolute left-0 top-0 z-[10] hidden h-full w-[250px] sm:block',
              'ea-products-in-sale__left-gradient',
            )}
          />
          <Carousel
            options={options}
            enableLoop
            className={{
              main: 'w-full',
              container: 'w-full',
              wrapper: 'w-full',
              slide: 'mx-2 w-full max-w-[282px] sm:mx-2.5 lg:mx-3',
            }}
            slides={products.map(product => (
              <ProductCard product={product} key={product.id} white imageSizes="282px" />
            ))}
          />
          <div
            className={cn(
              'absolute right-0 top-0 z-[10] hidden h-full w-[250px] sm:block',
              'ea-products-in-sale__right-gradient',
            )}
          />
        </div>
      )}
    </section>
  );
}
